import StyledCheckoutFormRow from '@components/Organisms/OrderForms/styled/StyledCheckoutFormRow';
import React, { FC, useCallback, useState } from 'react';
import { Heading } from '@components/Atoms/Heading';
import StyledStuckInAContractForm from './styled/StyledStuckInAContractForm';
import StyledContainer from './styled/StyledContainer';
import { CheckoutFormRow } from '@components/Organisms/OrderForms/CheckoutFormRow';
import { Button } from '@components/Atoms';
import { ButtonType } from '@components/Atoms/Button/types';
import StyledButtonRow from '@components/Organisms/OrderForms/styled/StyledButtonRow';
import StyledForm from '@factories/RowFactory/components/StuckInAContractForm/styled/StyledForm';
import { productController } from '@controllers/index';
import { InputType } from '@components/Atoms/Input/types';
import { UprnItemT, UprnStatus } from '@lib/types';
import { PostcodeDropdown } from '@components/Organisms/Postcode/Postcode';
import { FakeInput } from '@components/Molecules/Hero/variants/Main/styled';
import StyledPostcodeContainer from '@components/Organisms/Postcode/styled/StyledPostcodeContainer';

export const StuckInAContractForm: FC<{
  Title: string;
  howWeUseYourDataText: string;
  display: boolean;
}> = ({ Title, howWeUseYourDataText, display }) => {
  const { customerCallMeServer } = productController;
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);

  const rows = [
    { key: 'contact_name', label: 'First Name', required: true, value: '' },
    { key: 'contact_surname', label: 'Last Name', required: true, value: '' },
    { key: 'email', label: 'Email', required: true, value: '' },
    { key: 'phone', label: 'Phone Number', required: true, value: '' },
    { key: 'current_provider', label: 'Current Provider', required: true, value: '' },
    {
      key: 'ending_current',
      label: 'Contract end dates',
      required: true,
      value: '',
      placeholder: 'Date of birth',
      type: 'date',
      autocomplete: 'bday',
    },
    {
      key: 'postcode',
      type: 'postcode',
      required: true,
      value: '',
    },
    {
      key: 'uprn',
      type: 'postcode',
      required: true,
      value: '',
    },
  ];

  const defaultFormData = rows.reduce((acc, row) => {
    return {
      ...acc,
      [row.key]: row.value,
    };
  }, {});

  const [formData, setFormData] = useState<any>(defaultFormData);

  const formDataValues: Array<{
    [key: string]: string;
  }> = Object.values(formData);

  const contractEndDate = new Date(formData.ending_current);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const hasExpiredContract = contractEndDate < today;

  const validators = [
    {
      keys: [],
      isValid: formDataValues.every((field) => field),
      message: 'Field is required',
    },
    {
      keys: ['email'],
      isValid: formData.email?.match(/^[^\s@]+@([^\s@.,]+\.)+[^\s@.,]{2,}$/),
      message: 'Email is invalid',
    },
    {
      keys: ['phone'],
      isValid: formData.phone?.match(
        /^(((\+44\s?\d{4}|\(?0\d{4}\)?)\s?\d{3}\s?\d{3})|((\+44\s?\d{3}|\(?0\d{3}\)?)\s?\d{3}\s?\d{4})|((\+44\s?\d{2}|\(?0\d{2}\)?)\s?\d{4}\s?\d{4}))(\s?#(\d{4}|\d{3}))?$/,
      ),
      message: 'Phone is invalid',
    },
    {
      keys: ['ending_current'],
      isValid: formData.ending_current?.match(/^\d{4}-\d{2}-\d{2}$/),
      message: 'Please enter a valid date. ',
    },
    {
      keys: ['ending_current'],
      isValid: !hasExpiredContract,
      message:
        'Your contract seems to have ended already. That means you can sign up with us right away!',
    },
  ];

  const isValid = validators.every((validator) => validator.isValid);

  const submitForm = useCallback(async () => {
    setHasSubmitted(true);

    try {
      await customerCallMeServer({ args: formData }).then((responseCallMe) => {
        console.log({ responseCallMe });
      });
    } catch (error) {
      console.log(error);
      setHasSubmitted(false);
    }
  }, [customerCallMeServer, formData]);

  const updateFormData = (key: string, value: any) => {
    setFormData((prev) => {
      return {
        ...prev,
        [key]: value,
      };
    });
  };

  const [uprns, setUprns] = useState<UprnItemT[] | null>(null);
  const [uprn, setUprn] = useState<UprnItemT | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentPostcode, setCurrentPostcode] = useState<string>('');
  const [showPostcodeDropdown, setShowPostcodeDropdown] = useState<boolean>(false);

  const validatePostcode = async () => {
    setIsLoading(true);
    setUprn(null);
    setShowPostcodeDropdown(true);

    try {
      const postcodeCheck = await productController.checkPostcode({ postcode: currentPostcode });
      const uprnList = await productController.getUprnListServer({ postcode: currentPostcode });

      updateFormData('postcode', currentPostcode);

      if (uprnList?.length) {
        setUprns(uprnList);
      } else if (postcodeCheck) {
        setUprns([
          {
            uprn: '',
            address: `${postcodeCheck.admin_ward}, ${postcodeCheck.admin_district}, ${postcodeCheck.postcode}`,
            coverage: UprnStatus.NOTPASSED,
            address_type: '',
            postcode: '',
            ismdu: false,
          },
        ]);
      }
    } catch (e) {
      updateFormData('postcode', '');
      updateFormData('uprn', '');
      setUprns(null);
    }

    setIsLoading(false);
  };

  const selectUprn = (uprn: UprnItemT) => {
    setUprn(uprn);
    updateFormData('uprn', uprn?.uprn);
    setShowPostcodeDropdown(false);
  };

  if (!display) {
    return null;
  }

  return (
    <StyledStuckInAContractForm id="form">
      <StyledContainer>
        <header className="mb-10 md:mb-4">
          <Heading level={2}>{Title}</Heading>
        </header>
        {hasSubmitted ? (
          <p className="submittedText">
            Thank you! Your request has been received. Our team will contact you shortly with your
            no-obligation contract buyout quote.
          </p>
        ) : (
          <form className="flex flex-col items-center" onSubmit={submitForm}>
            <StyledForm>
              {rows
                .filter(({ type }) => type !== 'checkbox' && type !== 'postcode')
                .map(({ key, label, type, required, placeholder }) => (
                  <CheckoutFormRow
                    className="w-1/2"
                    key={key}
                    name={key}
                    label={label}
                    formData={formData}
                    validators={validators}
                    onChange={updateFormData}
                    type={type}
                    required={required}
                    placeholder={placeholder}
                  />
                ))}

              <StyledCheckoutFormRow className="w-1/2 relative">
                <StyledPostcodeContainer showShadow={showPostcodeDropdown}>
                  <div className={'relative z-1 bg-white rounded-xl min-w-full'}>
                    {uprn || showPostcodeDropdown ? (
                      <div
                        className={
                          showPostcodeDropdown
                            ? 'flex justify-between items-center py-6 border-b-[1px] border-[rgba(#707070, 0.2)] mb-4 px-8'
                            : 'flex justify-between items-center py-6 px-4 stuck-in-show-address'
                        }
                      >
                        <p
                          className={`text-black uppercase ${
                            showPostcodeDropdown ? 'font-bold ' : ''
                          }`}
                        >
                          {showPostcodeDropdown ? currentPostcode : uprn?.address}
                        </p>
                        <button
                          className="underline text-primary font-geomanist"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            setShowPostcodeDropdown(false);
                            setUprn(null);
                            updateFormData('postcode', '');
                            updateFormData('uprn', '');
                          }}
                        >
                          Change postcode
                        </button>
                      </div>
                    ) : (
                      <FakeInput isStandalone={false} withBorder={false} className="postcodeInput">
                        <input
                          type={InputType.TEXT}
                          key="postcode"
                          name="postcode"
                          required={true}
                          placeholder="Enter your postcode"
                          value={currentPostcode}
                          onChange={(e) => setCurrentPostcode(e.target.value)}
                        />
                        <Button
                          buttonType={ButtonType.ALTERNATE}
                          type="button"
                          onClick={validatePostcode}
                        >
                          Search
                        </Button>
                      </FakeInput>
                    )}
                    <PostcodeDropdown
                      uprns={uprns}
                      isLoading={isLoading}
                      onSelect={selectUprn}
                      isSubmitted={showPostcodeDropdown}
                    />
                  </div>
                </StyledPostcodeContainer>
              </StyledCheckoutFormRow>
            </StyledForm>

            {howWeUseYourDataText ? (
              <p className="howWeUseYourDataText">{howWeUseYourDataText}</p>
            ) : null}
            <StyledButtonRow>
              <Button buttonType={ButtonType.PRIMARY} type="submit" disabled={!isValid}>
                Submit
              </Button>
            </StyledButtonRow>
          </form>
        )}
      </StyledContainer>
    </StyledStuckInAContractForm>
  );
};
